// British Columbia Security Services — Services Data
// Each service is a full SEO landing page profile.

export const SERVICES = [
  {
    slug: "mobile-patrol",
    name: "Mobile Patrol",
    tagline: "Randomized route patrols across British Columbia — visible deterrence, verifiable checkpoints.",
    image: "https://images.pexels.com/photos/27299956/pexels-photo-27299956.jpeg",
    number: "01",
    keywords: [
      "mobile patrol security BC",
      "mobile patrol guard British Columbia",
      "vehicle patrol security services BC",
      "24/7 mobile patrol Vancouver",
      "GPS tracked patrol BC",
      "random patrol security British Columbia",
    ],
    overview:
      "Marked patrol vehicles conducting randomized, GPS-verified checks across residential complexes, construction sites, industrial yards, and commercial parking lots throughout British Columbia. Every stop is logged with time, geolocation, and incident notes accessible through a client portal.",
    scope: [
      "Randomized patrol routes with GPS verification",
      "Marked security vehicles for maximum visibility",
      "Interior and exterior building checks",
      "Lock-up and unlock services",
      "Alarm response as first responder",
      "Digital incident reporting with timestamps and photos",
      "Overnight and off-hour coverage across all BC regions",
    ],
    industries: [
      "Strata and residential complexes",
      "Construction sites",
      "Commercial retail plazas",
      "Industrial warehousing",
      "Auto dealerships",
      "Schools and campuses",
    ],
    faq: [
      {
        q: "How often will patrols visit my site?",
        a: "Frequency is scoped to risk. Typical contracts run 2–8 randomized visits per night, with GPS-verified check-in points on every visit.",
      },
      {
        q: "Do you provide reporting?",
        a: "Yes. Every patrol generates a timestamped digital report with GPS coordinates, photographic evidence when relevant, and any incident notes, delivered to your inbox by morning.",
      },
      {
        q: "What is your response time to an incident?",
        a: "Mobile patrol officers dispatched from active routes typically arrive within 15–25 minutes across metro BC.",
      },
    ],
  },
  {
    slug: "static-uniformed-guards",
    name: "Static & Uniformed Guards",
    tagline: "Licensed, uniformed security officers stationed on-site across British Columbia.",
    image: "https://images.pexels.com/photos/8285775/pexels-photo-8285775.jpeg",
    number: "02",
    keywords: [
      "uniformed security guards BC",
      "static security services British Columbia",
      "on-site security guards Vancouver",
      "licensed security officers BC",
      "24 hour security guard British Columbia",
    ],
    overview:
      "Uniformed, BC-licensed security officers stationed at your facility for continuous coverage — access control, visitor management, CCTV monitoring, and incident response. Every officer holds a valid BC Security Worker License and completes site-specific orientation before deployment.",
    scope: [
      "Access control and visitor screening",
      "CCTV monitoring and dispatch",
      "Foot patrols within the property boundary",
      "Emergency response and evacuation coordination",
      "Written and digital daily activity reports",
      "Concierge-style front-desk duties on request",
    ],
    industries: [
      "Corporate offices",
      "Government buildings",
      "Hospitals and clinics",
      "Warehouses and logistics hubs",
      "Hotels and hospitality",
      "Educational institutions",
    ],
    faq: [
      {
        q: "Are guards licensed in British Columbia?",
        a: "Yes. Every officer holds a current BC Security Worker License issued by the Security Programs Division and completes background verification before deployment.",
      },
      {
        q: "Can guards perform concierge duties?",
        a: "Absolutely. Many corporate and residential clients pair concierge duties with security responsibilities for a professional single-point-of-contact.",
      },
    ],
  },
  {
    slug: "construction-site-security",
    name: "Construction Site Security",
    tagline: "Theft, vandalism, and after-hours protection for BC construction sites.",
    image: "https://images.unsplash.com/photo-1693661391267-ad955aeeb564",
    number: "03",
    keywords: [
      "construction security BC",
      "construction site security guards Vancouver",
      "job site security British Columbia",
      "construction theft prevention BC",
      "after hours construction security",
    ],
    overview:
      "Copper, tools, lumber, and heavy equipment are prime targets across BC construction sites. We deploy static overnight guards, randomized mobile patrols, and temporary CCTV to protect assets during framing, servicing, and finishing stages.",
    scope: [
      "Overnight static guard posts",
      "Randomized mobile patrol coverage",
      "Trailer and material yard checks",
      "Access log for trades and subcontractors",
      "Fire watch during hot work windows",
      "Temporary CCTV and remote monitoring",
    ],
    industries: [
      "Residential mid-rise and high-rise",
      "Commercial construction",
      "Infrastructure projects",
      "Renovation and tenant improvement",
      "Public works",
    ],
    faq: [
      {
        q: "Can you scale coverage as the project progresses?",
        a: "Yes — most contracts start with heavy mobile patrol during framing and transition to static overnight coverage during finishing when high-value materials are on site.",
      },
    ],
  },
  {
    slug: "retail-loss-prevention",
    name: "Retail & Loss Prevention",
    tagline: "Plain-clothes and uniformed loss prevention officers for BC retail.",
    image: "https://images.unsplash.com/photo-1604719312566-8912e9227c6a",
    number: "04",
    keywords: [
      "loss prevention BC",
      "retail security guards Vancouver",
      "shoplifter apprehension British Columbia",
      "store security BC",
      "mall security guards British Columbia",
    ],
    overview:
      "Trained loss prevention officers work in-store — uniformed as a visible deterrent or plain-clothes to observe and lawfully apprehend organized theft. Officers are trained in the BC Trespass Act, CCROC, and lawful shopkeeper's arrest procedure.",
    scope: [
      "Uniformed store greeters and deterrence officers",
      "Plain-clothes loss prevention",
      "CCTV live-monitoring shifts",
      "Organized retail crime documentation",
      "Court-ready incident reports and statements",
      "Coordination with RCMP and municipal police",
    ],
    industries: [
      "Grocery",
      "Big-box retail",
      "Specialty apparel",
      "Electronics",
      "Cannabis retail",
      "Pharmacy",
    ],
    faq: [],
  },
  {
    slug: "event-security",
    name: "Event Security",
    tagline: "Concerts, festivals, conferences, weddings — coordinated event security province-wide.",
    image: "https://images.unsplash.com/photo-1556764420-e37ef4cdfa5c",
    number: "05",
    keywords: [
      "event security BC",
      "concert security British Columbia",
      "festival security Vancouver",
      "wedding security BC",
      "conference security guards British Columbia",
    ],
    overview:
      "From private galas to 10,000-capacity festivals — pre-event risk assessment, credentialed access, crowd flow management, bag checks, medical liaison, and post-event escorted egress. All officers hold Advanced Security Training (AST) credentials as required by BC event scale.",
    scope: [
      "Pre-event security planning and risk assessment",
      "Credentialed access and wristbanding",
      "Bag checks and magnetometer screening",
      "Perimeter and stage security",
      "VIP and artist escort",
      "Crowd flow and egress management",
    ],
    industries: [
      "Live music and festivals",
      "Corporate conferences",
      "Sporting events",
      "Weddings and private celebrations",
      "Trade shows",
      "Political events",
    ],
    faq: [],
  },
  {
    slug: "corporate-concierge",
    name: "Corporate & Concierge Security",
    tagline: "Professional front-of-house security for corporate campuses and residential towers.",
    image: "https://images.unsplash.com/photo-1758448656987-cfae6bf225e4",
    number: "06",
    keywords: [
      "corporate security BC",
      "concierge security Vancouver",
      "office building security British Columbia",
      "residential concierge BC",
      "front desk security guards",
    ],
    overview:
      "Suit-and-tie professional concierge officers double as your first line of security — greeting guests, managing deliveries, monitoring access, and responding to incidents while preserving the brand experience your tenants expect.",
    scope: [
      "Visitor registration and badge issuance",
      "Package and courier logging",
      "Elevator and floor-access control",
      "Emergency response coordination",
      "Tenant services and after-hours support",
      "Detailed shift handover logs",
    ],
    industries: [
      "Class-A office towers",
      "Residential high-rise",
      "Mixed-use developments",
      "Corporate headquarters",
      "Government buildings",
    ],
    faq: [],
  },
  {
    slug: "residential-security",
    name: "Residential Security",
    tagline: "Strata, single-family, and gated community security across British Columbia.",
    image: "https://images.unsplash.com/photo-1568092775960-f88fe72d8f00",
    number: "07",
    keywords: [
      "residential security BC",
      "strata security Vancouver",
      "gated community security British Columbia",
      "home security guards BC",
      "neighborhood patrol British Columbia",
    ],
    overview:
      "Overnight patrols, parking enforcement, incident response, and visible deterrence for stratas, townhome complexes, and gated neighborhoods across BC. Every patrol delivers a morning report to the strata council or property manager.",
    scope: [
      "Nightly patrol visits",
      "Parking bylaw enforcement",
      "Common-area lock-up",
      "Trespasser removal",
      "Package theft deterrence",
      "Council incident reporting",
    ],
    industries: [
      "Stratas",
      "Townhome complexes",
      "Gated communities",
      "Vacation properties",
      "Luxury single-family residences",
    ],
    faq: [],
  },
  {
    slug: "alarm-response",
    name: "Alarm Response",
    tagline: "24/7 first-responder alarm dispatch across British Columbia.",
    image: "https://images.unsplash.com/photo-1521790945508-bf2a36314e73",
    number: "08",
    keywords: [
      "alarm response BC",
      "alarm response security Vancouver",
      "24 hour alarm response British Columbia",
      "keyholder services BC",
      "alarm dispatch guards",
    ],
    overview:
      "When an alarm triggers, we roll first. Licensed officers dispatched to your site to verify the alarm, secure the premises, coordinate with police, and return the site to a locked state — sparing you the 3 a.m. drive.",
    scope: [
      "24/7 dispatch from mobile patrol fleet",
      "Interior and exterior verification",
      "Police liaison for confirmed breaches",
      "Site re-securing and lock-up",
      "Written incident report",
    ],
    industries: [
      "Commercial retail",
      "Warehouses",
      "Offices",
      "Multi-family residential",
      "Industrial facilities",
    ],
    faq: [],
  },
  {
    slug: "mining-security",
    name: "Mining Security",
    tagline: "Remote camp, pit, and mill security for BC mining operations.",
    image: "https://images.unsplash.com/photo-1628487749130-2d41acb1802a",
    number: "09",
    keywords: [
      "mining security BC",
      "mine site security British Columbia",
      "remote mining camp security",
      "mining security guards BC",
      "resource extraction security British Columbia",
      "mine gatehouse security BC",
      "mining industry security services",
    ],
    overview:
      "British Columbia's mineral belt runs from the Kootenays to the Northwest — copper, gold, coal, zinc, and molybdenum operations that demand hardened, remote-ready security. We staff gatehouses, screen contractors, protect pit and mill infrastructure, and provide fire watch during hot work and blasting windows. Officers are pre-cleared for camp accommodation, MSHA/WorkSafeBC oriented, and comfortable with fly-in fly-out rotations.",
    scope: [
      "Main gate and secondary access control",
      "Contractor and vehicle screening",
      "Pit rim and haul road patrols",
      "Mill and processing plant security",
      "Explosive magazine watch",
      "Fire watch during blasting and hot work",
      "Camp security and dry-camp enforcement",
      "Environmental protest response protocols",
      "Fly-in fly-out rotations (14/14, 21/7)",
    ],
    industries: [
      "Copper and gold mines",
      "Coal operations",
      "Aggregate quarries",
      "Exploration camps",
      "Processing mills",
      "Tailings management facilities",
    ],
    faq: [
      {
        q: "Do your officers work fly-in fly-out?",
        a: "Yes. We rotate teams on 14/14 and 21/7 schedules with full camp accommodation, and coordinate with your logistics team on charter or scheduled flights.",
      },
      {
        q: "Are officers oriented to WorkSafeBC and site-specific safety?",
        a: "Every officer completes site-specific H&S orientation, wears PPE per site requirements, and operates within your ERP.",
      },
      {
        q: "Can you handle protest and blockade response?",
        a: "Yes — we plan lawful, de-escalation-focused response protocols aligned with your permits and community engagement strategy.",
      },
    ],
  },
  {
    slug: "industrial-security",
    name: "Industrial Security",
    tagline: "Warehouses, refineries, ports, and manufacturing plants across BC.",
    image: "https://images.unsplash.com/photo-1580982327559-c1202864eb05",
    number: "10",
    keywords: [
      "industrial security BC",
      "warehouse security British Columbia",
      "manufacturing plant security BC",
      "refinery security British Columbia",
      "logistics facility security",
    ],
    overview:
      "Perimeter integrity, driver check-in, load-out verification, and 24/7 gatehouse coverage for BC industrial facilities. Every driver logged. Every load reconciled. Every incident reported.",
    scope: [
      "Gatehouse and access control",
      "Driver and vehicle inspection",
      "Load-out reconciliation",
      "Perimeter patrol",
      "CCTV live-monitoring",
      "Emergency response coordination",
    ],
    industries: [
      "Distribution centers",
      "Refineries",
      "Manufacturing plants",
      "Cold storage",
      "Logistics hubs",
    ],
    faq: [],
  },
  {
    slug: "marine-port-security",
    name: "Marine & Port Security",
    tagline: "Terminal, marina, and vessel security along British Columbia's coast.",
    image: "https://images.unsplash.com/photo-1605732562742-3023a888e56e",
    number: "11",
    keywords: [
      "marine security BC",
      "port security British Columbia",
      "terminal security Vancouver",
      "marina security BC",
      "vessel security British Columbia",
      "waterfront security guards",
    ],
    overview:
      "From the Port of Vancouver to Prince Rupert, marinas across Vancouver Island, and remote terminals — coordinated marine security aligned with Marine Transportation Security Regulations (MTSR) and Transport Canada requirements.",
    scope: [
      "MTSR-compliant terminal patrols",
      "Vessel and gangway security",
      "Marina and moorage checks",
      "Restricted area access control",
      "Coordination with Canadian Coast Guard and CBSA",
      "Dockside CCTV monitoring",
    ],
    industries: [
      "Container terminals",
      "Bulk terminals",
      "Cruise terminals",
      "Marinas and yacht clubs",
      "Coastal ferry terminals",
      "Fishing docks",
    ],
    faq: [],
  },
  {
    slug: "cannabis-facility-security",
    name: "Cannabis Facility Security",
    tagline: "Licensed producer, retail, and cultivation site security in British Columbia.",
    image: "https://images.unsplash.com/photo-1603909223429-69bb7101f420",
    number: "12",
    keywords: [
      "cannabis security BC",
      "cannabis dispensary security British Columbia",
      "licensed producer security BC",
      "cannabis facility guards British Columbia",
      "cannabis cultivation security",
    ],
    overview:
      "Health Canada and BC LDB compliance-focused security for cultivation, processing, and retail cannabis. Video retention, restricted access, and inventory movement protocols aligned with the Cannabis Act.",
    scope: [
      "Access control and biometric verification",
      "Vault and safe monitoring",
      "Employee bag check protocols",
      "Retail floor security",
      "Delivery escort",
      "Cannabis Act compliant reporting",
    ],
    industries: [
      "Licensed producers",
      "Cannabis retail stores",
      "Processing facilities",
      "Cultivation greenhouses",
    ],
    faq: [],
  },
  {
    slug: "executive-protection",
    name: "Executive Protection",
    tagline: "Discreet, close-protection details for executives, dignitaries, and high-net-worth clients.",
    image: "https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e",
    number: "13",
    keywords: [
      "executive protection BC",
      "close protection Vancouver",
      "bodyguard services British Columbia",
      "VIP security BC",
      "personal security guards British Columbia",
    ],
    overview:
      "Advance-team route planning, low-profile close protection, secure ground transport, and residential coverage for executives, dignitaries, entertainers, and high-net-worth families visiting or residing in British Columbia.",
    scope: [
      "Threat and route assessment",
      "Close protection details (armed or unarmed as jurisdiction allows)",
      "Secure ground transportation",
      "Residential security details",
      "Family and school-run coverage",
      "Coordination with corporate security teams",
    ],
    industries: [
      "C-suite executives",
      "Entertainment industry",
      "Diplomatic visits",
      "Sports figures",
      "High-net-worth families",
    ],
    faq: [],
  },
  {
    slug: "fire-watch",
    name: "Fire Watch",
    tagline: "AHJ-compliant fire watch when your fire system is offline.",
    image: "https://images.unsplash.com/photo-1601058272104-e6d2fcd4dbcf",
    number: "14",
    keywords: [
      "fire watch BC",
      "fire watch security British Columbia",
      "impaired fire system BC",
      "fire watch guards Vancouver",
      "hot work fire watch",
    ],
    overview:
      "When sprinklers are drained, alarms are down, or hot work is in progress — dedicated fire watch officers patrol on required intervals, log every round, and coordinate with the Authority Having Jurisdiction (AHJ) to keep your building occupancy legal.",
    scope: [
      "15-minute, 30-minute, or hourly interval rounds",
      "Written and digital logs for AHJ inspection",
      "Extinguisher placement verification",
      "Hot-work permit coordination",
      "Emergency 911 dispatch capability",
    ],
    industries: [
      "Construction",
      "Renovation projects",
      "Hospitals and long-term care",
      "Industrial facilities",
      "Commercial buildings",
    ],
    faq: [],
  },
  {
    slug: "parking-enforcement",
    name: "Parking Enforcement",
    tagline: "Bylaw and private property parking enforcement across BC.",
    image: "https://images.unsplash.com/photo-1449965408869-eaa3f722e40d",
    number: "15",
    keywords: [
      "parking enforcement BC",
      "private property parking British Columbia",
      "parking security Vancouver",
      "towing coordination BC",
      "parking bylaw enforcement British Columbia",
    ],
    overview:
      "Signage-compliant enforcement, ticketing, and tow coordination for private lots, stratas, and commercial properties across BC. Every violation photographed and logged for legal defensibility.",
    scope: [
      "Signage audit and installation guidance",
      "Ticketing on marked private property",
      "Tow coordination with licensed operators",
      "Permit and decal management",
      "Photographic evidence and dispute records",
    ],
    industries: [
      "Strata parking lots",
      "Commercial plazas",
      "Office buildings",
      "Medical clinics",
      "Retail lots",
    ],
    faq: [],
  },
  {
    slug: "loss-investigations",
    name: "Loss & Fraud Investigations",
    tagline: "Discreet workplace and retail investigations across British Columbia.",
    image: "https://images.unsplash.com/photo-1450101499163-c8848c66ca85",
    number: "16",
    keywords: [
      "workplace investigations BC",
      "fraud investigations British Columbia",
      "internal theft investigations BC",
      "loss investigations Vancouver",
      "licensed private investigators BC",
    ],
    overview:
      "Licensed BC private investigators handle workplace theft, WCB fraud, insurance claims, and civil evidence gathering — with reports and testimony structured to hold up in tribunal and court.",
    scope: [
      "Covert surveillance",
      "Interview and statement collection",
      "Digital and physical evidence chain-of-custody",
      "Report writing for HR and legal counsel",
      "Court and tribunal testimony",
    ],
    industries: [
      "Corporate HR",
      "Insurance",
      "Retail loss prevention",
      "Legal counsel",
    ],
    faq: [],
  },
];

export const getServiceBySlug = (slug) => SERVICES.find((s) => s.slug === slug);
